import { useMemo } from 'react';
import { PhoneNumbersV1 } from '@frontend/api-phone-numbers';
import { useAppFlagStore } from '@frontend/shared';
import { LocationPhone, LocationPhonesData } from '../types';
import { formatProtobufPhone } from '../utils';
import { useInboxLocationIds } from './use-inbox-location-ids';

type UseLocationPhonesArgs = {
  locationIds?: string[];
  filterLocationPhones?: string[];
};

type UseLocationPhonesReturn = {
  data: LocationPhonesData;
  enabledData: LocationPhonesData;
  isLoading: boolean;
};

/**
 * Hook for fetching and managing outbound phone numbers available in the inbox.
 *
 * This hook provides a unified interface for retrieving either all sms-enabled phone numbers, or only
 * department-related phone numbers SMS numbers or general location phone numbers, depending on feature flag
 * configuration. It handles ACL filtering to ensure users only see phone numbers for the locations that they have
 * inbox permissions for.
 *
 * @param locationIds - Optional array of location IDs to filter phone numbers.
 *   If not provided, uses currently selected location IDs.
 *
 * @returns Object containing:
 *   - `data`: The location phones data
 *
 * @remarks
 * - Phone numbers are formatted to E164 standard for consistency
 * - Queries are conditionally enabled based on feature flag state and location availability
 */
export const useLocationPhones = ({
  locationIds: providedLocationIds,
  filterLocationPhones = [],
}: UseLocationPhonesArgs = {}): UseLocationPhonesReturn => {
  const { getFeatureFlagValue } = useAppFlagStore();
  const locationIds = useInboxLocationIds({ locationIds: providedLocationIds });

  const isPrivateCommsEnabled = getFeatureFlagValue({
    locationIds,
    flagName: 'apply-phone-number-user-access-restriction',
    strategy: 'ALL',
  });

  const allLocationPhonesQuery = PhoneNumbersV1.Queries.useListPhoneNumbersForLocationsQuery({
    request: {
      locationIds,
      sms: true,
      excludeTenDlc: true,
    },
    options: {
      enabled: !isPrivateCommsEnabled && locationIds.length > 0,
    },
  });

  const userAccessibleLocationPhonesQuery = PhoneNumbersV1.Queries.useListUserAccessiblePhoneNumbersQuery({
    request: {
      locationIds,
    },
    options: {
      enabled: isPrivateCommsEnabled && locationIds.length > 0,
    },
  });

  const locationPhonesQuery = isPrivateCommsEnabled ? userAccessibleLocationPhonesQuery : allLocationPhonesQuery;

  const outboundSmsNumbersQuery = PhoneNumbersV1.Queries.useListOutboundSmsNumbers(
    {
      locationIds,
    },
    {
      enabled: locationIds.length > 0,
    }
  );

  // For private comms, filter outbound SMS numbers to only include those present in user accessible phone numbers
  // If locationPhonesQuery fails or is loading, return empty array to prevent showing unverified numbers
  const filteredOutboundSmsNumbers = useMemo(() => {
    // If location phones query hasn't succeeded, don't show any outbound numbers
    if (!isPrivateCommsEnabled || !locationPhonesQuery.isSuccess || !locationPhonesQuery.data?.phoneNumbers) {
      return [];
    }

    // If outbound SMS query hasn't loaded yet or failed, return empty array
    if (!outboundSmsNumbersQuery.data) {
      return [];
    }

    // Create a Set of accessible phone numbers for efficient lookup
    const accessiblePhoneNumbers = new Set(
      locationPhonesQuery.data.phoneNumbers.map((phone) => formatProtobufPhone(phone.phoneNumber)).filter(Boolean)
    );

    // Filter outbound SMS numbers to only include those in the accessible set
    return outboundSmsNumbersQuery.data
      .map(({ locationId, outboundSmsNumbers: currentOutboundSmsNumbers }) => ({
        locationId,
        outboundSmsNumbers: (currentOutboundSmsNumbers || []).filter(({ number }) => {
          // Only include if the number exists in user's accessible phones
          return number && accessiblePhoneNumbers.has(number);
        }),
      }))
      .filter((entry) => entry.outboundSmsNumbers.length > 0); // Remove locations with no matching numbers
  }, [
    isPrivateCommsEnabled,
    locationPhonesQuery.isSuccess,
    locationPhonesQuery.data?.phoneNumbers,
    outboundSmsNumbersQuery.data,
  ]);

  const outboundSmsNumbers = useMemo(
    () => (isPrivateCommsEnabled ? filteredOutboundSmsNumbers : outboundSmsNumbersQuery.data || []),
    [filteredOutboundSmsNumbers, isPrivateCommsEnabled, outboundSmsNumbersQuery.data]
  );

  const filterLocationPhonesStr = filterLocationPhones.join(',');
  const allLocationPhonesData = useMemo<LocationPhonesData>(() => {
    // v4 - outbound sms numbers
    return (
      locationPhonesQuery.data?.phoneNumbers?.reduce<LocationPhonesData>(
        (acc, curr) => {
          if (!locationIds.includes(curr.locationId)) {
            return acc;
          }
          const currentEntries = acc.locationPhonesByLocation[curr.locationId] ?? [];
          const locationPhone = formatProtobufPhone(curr.phoneNumber);

          if (!locationPhone || filterLocationPhonesStr.includes(locationPhone)) {
            return acc;
          }

          const newEntry: LocationPhone = {
            locationPhone,
            locationId: curr.locationId,
            displayName: curr.displayName,
            defaultSms: curr.defaultSms,
            defaultAi: curr.defaultAi,
          };

          acc.locationPhonesByLocation[curr.locationId] = [...currentEntries, newEntry];
          acc.allLocationPhones.push(newEntry);
          return acc;
        },
        { locationPhonesByLocation: {}, allLocationPhones: [] }
      ) ?? { locationPhonesByLocation: {}, allLocationPhones: [] }
    );
  }, [filterLocationPhonesStr, locationIds, locationPhonesQuery.data?.phoneNumbers]);

  const enabledOutboundPhoneNumbers = useMemo<LocationPhonesData>(() => {
    // v4 - outbound sms numbers (filtered by user accessible phones)
    return outboundSmsNumbers
      .map(({ locationId, outboundSmsNumbers: currentOutboundSmsNumbers }) =>
        (currentOutboundSmsNumbers || []).map((number) => ({ ...number, locationId }))
      )
      .flat()
      .reduce<LocationPhonesData>(
        (acc, curr) => {
          if (!locationIds.includes(curr.locationId)) {
            return acc;
          }

          const currentEntries = acc.locationPhonesByLocation[curr.locationId] ?? [];

          if (!curr.number || filterLocationPhonesStr.includes(curr.number)) {
            return acc;
          }

          const newEntry: LocationPhone = {
            locationPhone: curr.number,
            locationId: curr.locationId,
            displayName: curr.displayName,
            defaultSms: curr.default,
          };

          acc.locationPhonesByLocation[curr.locationId] = [...currentEntries, newEntry];
          acc.allLocationPhones.push(newEntry);
          return acc;
        },
        { locationPhonesByLocation: {}, allLocationPhones: [] }
      );
  }, [filterLocationPhonesStr, locationIds, outboundSmsNumbers]);

  return {
    data: allLocationPhonesData,
    enabledData: enabledOutboundPhoneNumbers,
    isLoading: locationPhonesQuery.isLoading,
  };
};
